import { reportingPrisma as prisma } from "client";
interface Result {
  summary: SummaryResult[];
  totals: Totals[];
}
interface SummaryResult {
  title: string;
  metric: string;
}
interface Totals {
  date: string;
  Events: number;
  "White Labels": number;
}
export const getWhiteLabelAndEventSummary = async () => {
  try {
    const whiteLabelCount: SummaryResult | any = await getWhiteLabelCount();
    const OrganizersCount: Result | any = await getOrganizersCount();
    const eventsCount: Result | any = await getEventsCount();
    if (!whiteLabelCount || !eventsCount) {
      return { result: [], totals: [] };
    }

    const result: SummaryResult[] = [
      ...whiteLabelCount,
      ...OrganizersCount?.summary,
      ...eventsCount?.summary,
    ];

    const totals: Totals[] = mergeTotalsArray(
      eventsCount?.totals || [],
      whiteLabelCount?.totals || []
    );

    return { result, totals };
  } catch (e) {
    console.error(e);
  }
};

export const getWhiteLabelCount = async () => {
  const stages = [
    { $sort: { _id: -1 } },
    {
      $facet: {
        summary: [
          {
            $group: {
              _id: "$isActive",
              metric: { $sum: 1 },
            },
          },
          {
            $addFields: {
              title: {
                $cond: {
                  if: {
                    $or: [
                      { $eq: ["$_id", true] },
                      { $eq: ["$_id", null] }, // Consider null as active
                    ],
                  },
                  then: "Active White Labels",
                  else: "Inactive White Labels",
                },
              },
              order: {
                $cond: {
                  if: {
                    $or: [
                      { $eq: ["$_id", true] },
                      { $eq: ["$_id", null] }, // Consider null as active
                    ],
                  },
                  then: 1,
                  else: 2,
                },
              },
            },
          },
          {
            $project: {
              _id: 0,
              order: 1,
              metric: { $ifNull: ["$metric", 0] }, // Fill metric with 0 if it doesn't exist
              title: 1,
            },
          },
        ],
      },
    },
    {
      $project: {
        summary: {
          $concatArrays: [
            "$summary",
            [
              { metric: 0, title: "Active White Labels", order: 1 }, // Ensure Active White Labels entry
              { metric: 0, title: "Inactive White Labels", order: 2 }, // Ensure Inactive White Labels entry
            ],
          ],
        },
      },
    },
    { $unwind: "$summary" },
    { $replaceRoot: { newRoot: "$summary" } },
    {
      $group: {
        _id: "$title",
        order: { $first: "$order" },
        metric: { $sum: "$metric" },
        title: { $first: "$title" },
      },
    },
    {
      $sort: { order: 1 }, // Ensure the final output is sorted by order
    },
  ];

  const whiteLabelsCount = await prisma.whiteLabel.aggregateRaw({
    pipeline: [...stages],
  });

  return whiteLabelsCount;
};
export const getEventsCount = async () => {
  const stages = [
    { $sort: { _id: -1 } },
    {
      $group: {
        _id: null,
        total: { $sum: 1 },
        active: {
          $sum: {
            $cond: { if: { $eq: ["$published", true] }, then: 1, else: 0 },
          },
        },
        inactive: {
          $sum: {
            $cond: { if: { $eq: ["$published", false] }, then: 1, else: 0 },
          },
        },
      },
    },
    {
      $project: {
        _id: 0,
        summary: [
          { title: "All Events", metric: "$total" },
          { title: "Active Events", metric: "$active" },
          { title: "Draft Events", metric: "$inactive" },
        ],
      },
    },
  ];

  const EventsCount = await prisma.event.aggregateRaw({
    pipeline: [...stages],
  });

  return EventsCount[0];
};
export const getOrganizersCount = async () => {
  const stages = [
    { $sort: { _id: -1 } },
    {
      $group: {
        _id: null,
        total: { $sum: 1 },
      },
    },
    {
      $project: {
        _id: 0,
        summary: [{ title: "All Organizers", metric: "$total" }],
      },
    },
  ];
  const organizersCount = await prisma.organization.aggregateRaw({
    pipeline: [...stages],
  });

  return (
    organizersCount?.[0] ?? {
      summary: [{ title: "All Organizers", metric: 0 }],
    }
  );
};
function mergeTotalsArray(
  eventsArray: Totals[],
  whiteLabelsArray: Totals[]
): Totals[] {
  let resultData: Totals[] = [];
  for (const item of eventsArray) {
    const { Events } = item;
    const date = item.date;
    resultData.push({ date, Events, "White Labels": 0 });
  }

  for (const item of whiteLabelsArray) {
    const date = item.date;
    const whiteLabelsValue = item["White Labels"];

    const existingData = resultData.find((data) => data.date === date);

    if (existingData) {
      existingData["White Labels"] = whiteLabelsValue;
    } else {
      resultData.push({ date, "White Labels": whiteLabelsValue, Events: 0 });
    }
  }
  resultData.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateA - dateB;
  });
  return resultData;
}
